import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

import { environment } from '../../../environments/environment';
import { Brand } from '../../core/models/brand.model';
import { DataService } from '../../core/services/data.service';
import { UiService } from '../../core/services/ui.service';
import { AuthService } from '../../core/services/auth.service';
import { SessionService } from '../../core/services/session.service';
import { isValidEgPhone, normalizeEgPhone } from '../../core/services/phone.util';
import { SheetComponent, BtnComponent, IconComponent, SpinnerComponent, TranslatePipe } from '../../shared';

type Step = 'phone' | 'otp' | 'password' | 'reset' | 'profile' | 'location';
type Flow = 'register' | 'forgot';
type LocState = 'idle' | 'locating' | 'granted' | 'denied';

@Component({
  selector: 'app-login-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, SheetComponent, BtnComponent, IconComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './login-sheet.component.html',
  styles: [`
    .bb-login-h { margin: 0; font-family: var(--brand-display); font-weight: 700; font-size: 25px; letter-spacing: -0.02em; color: var(--fr-text); }
    .bb-login-p { margin: 8px 0 22px; font-size: 14px; line-height: 1.5; color: var(--fr-text-2); }
    .bb-login-lbl { font-size: 12.5px; font-weight: 700; color: var(--fr-text-2); display: block; margin-bottom: 7px; }
    .bb-login-link { border: none; background: none; color: var(--brand-deep); font-weight: 700; cursor: pointer; padding: 0; font-size: 14px; font-family: var(--fr-font); }
    .bb-login-err { font-size: 12px; color: var(--fr-danger); margin-top: 6px; }
    .bb-pw-field { display: flex; align-items: center; height: 54px; border-radius: 14px; background: var(--fr-surface); overflow: hidden; }
  `],
})
export class LoginSheetComponent implements OnInit, OnDestroy {
  open = false;
  reason = '';
  brand!: Brand;
  readonly demoOtp = environment.demoOtp;

  step: Step = 'phone';
  flow: Flow = 'register';
  phone = '';
  otp = '';
  timer = 0;
  busy = false;
  error = '';
  name = '';
  pw = '';
  pw2 = '';
  showPw = false;
  loginPw = '';
  tried = false;
  locState: LocState = 'idle';

  private registrationToken = '';
  private resetToken = '';
  private sub?: Subscription;
  private timerHandle: any = null;

  constructor(
    private data: DataService,
    private ui: UiService,
    private auth: AuthService,
    private session: SessionService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.data.getBrand().subscribe((b) => (this.brand = b));
    this.sub = this.ui.login$.subscribe((g) => {
      if (g.open && !this.open) this.resetAll();
      this.open = g.open;
      this.reason = g.reason;
      this.cdr.markForCheck();
    });
  }
  ngOnDestroy(): void { this.sub?.unsubscribe(); clearInterval(this.timerHandle); }

  private resetAll(): void {
    this.step = 'phone'; this.flow = 'register'; this.phone = ''; this.otp = '';
    this.busy = false; this.error = ''; this.name = ''; this.pw = ''; this.pw2 = ''; this.loginPw = '';
    this.tried = false; this.showPw = false; this.locState = 'idle'; this.timer = 0;
    this.registrationToken = ''; this.resetToken = '';
  }

  close(): void { this.ui.closeLogin(); }

  get pwMin(): number { return this.step === 'reset' ? 8 : 6; }
  get pwOk(): boolean { return this.pw.length >= this.pwMin; }
  get pwMatch(): boolean { return this.pw2 === this.pw && this.pw2.length > 0; }
  /** Translatable via the `t` pipe (dict has both the 6- and 8-char variants). */
  get pwPlaceholder(): string { return `At least ${this.pwMin} characters`; }
  get pwError(): string { return `Password must be at least ${this.pwMin} characters`; }

  onPhoneInput(v: string, el?: HTMLInputElement): void {
    this.phone = normalizeEgPhone(v);
    // A rejected keystroke (an 11th digit, or a leading 0) leaves the model
    // unchanged, so Angular skips the view write and the character would stay
    // on screen — push the cleaned value back into the field.
    if (el && el.value !== this.phone) el.value = this.phone;
    this.error = '';
  }
  /** Continue stays disabled until the local number is a full 10 digits. */
  get phoneComplete(): boolean { return isValidEgPhone(this.phone); }

  private startTimer(seconds: number): void {
    this.timer = seconds;
    clearInterval(this.timerHandle);
    this.timerHandle = setInterval(() => {
      this.timer = Math.max(0, this.timer - 1);
      if (this.timer === 0) clearInterval(this.timerHandle);
      this.cdr.markForCheck();
    }, 1000);
  }

  private fail(err: unknown): void {
    this.busy = false;
    const e = err as HttpErrorResponse;
    this.error = e?.error?.message || 'Something went wrong. Please try again.';
    this.cdr.markForCheck();
  }

  // ── step 1: phone ──
  continueFromPhone(): void {
    if (!this.phoneComplete || this.busy) return;
    this.busy = true; this.error = '';
    this.auth.checkPhone(this.phone).subscribe({
      next: (res) => {
        this.busy = false;
        if (res.action === 'login') { this.loginPw = ''; this.step = 'password'; }
        else this.goToOtp('register');
        this.cdr.markForCheck();
      },
      error: (e) => this.fail(e),
    });
  }

  /**
   * Move to OTP entry immediately (does not block on send-otp) and fire a
   * best-effort send in the background — if the SMS gateway is down the user
   * can still enter the code; a working gateway sends a real one.
   */
  goToOtp(flow: Flow): void {
    this.flow = flow;
    this.otp = '';
    this.error = '';
    this.step = 'otp';
    this.startTimer(60);
    this.requestOtp(false);
  }

  /** Resend the code (best-effort). Surfaces the error if the send fails. */
  resend(): void {
    this.otp = '';
    this.requestOtp(true);
  }

  private requestOtp(showErrors: boolean): void {
    const req = this.flow === 'forgot' ? this.auth.forgotPassword(this.phone) : this.auth.registerSendOtp(this.phone);
    req.subscribe({
      next: (res) => { this.startTimer(res.resend_available_in || 60); this.cdr.markForCheck(); },
      error: (e) => { if (showErrors) this.fail(e); },
    });
  }

  onOtpInput(v: string): void { this.otp = v.replace(/\D/g, '').slice(0, 6); this.error = ''; }

  /** Fill the input with the tenant's static test code. */
  fillDemoOtp(): void {
    if (!this.demoOtp) return;
    this.otp = this.demoOtp;
    this.error = '';
  }

  /** Verify the entered OTP (triggered by the Confirm button). */
  verifyOtp(): void {
    if (this.otp.length < 6 || this.busy) return;
    this.busy = true; this.error = '';
    const req = this.flow === 'forgot'
      ? this.auth.passwordVerifyOtp(this.phone, this.otp)
      : this.auth.registerVerifyOtp(this.phone, this.otp);
    req.subscribe({
      next: (res) => {
        this.busy = false;
        if (this.flow === 'forgot') { this.resetToken = res.reset_token || ''; this.step = 'reset'; }
        else { this.registrationToken = res.registration_token || ''; this.step = 'profile'; }
        this.tried = false; this.pw = ''; this.pw2 = '';
        this.cdr.markForCheck();
      },
      error: (e) => { this.otp = ''; this.fail(e); },
    });
  }

  // ── returning user: password login ──
  submitLogin(): void {
    this.busy = true; this.error = '';
    this.auth.login(this.phone, this.loginPw).subscribe({
      next: (res) => { this.session.setAuth(res.token, res.customer); this.busy = false; this.ui.completeLogin(); },
      error: (e) => {
        const err = e as HttpErrorResponse;
        if (err?.error?.error_code === 'phone_not_verified') { this.goToOtp('register'); return; }
        this.fail(e);
      },
    });
  }

  // ── new-user profile (completes registration + signs in) ──
  submitProfile(): void {
    if (!this.name.trim() || !this.pwOk || !this.pwMatch) { this.tried = true; return; }
    this.busy = true; this.error = '';
    this.auth.register(this.phone, this.registrationToken, this.name.trim(), this.pw).subscribe({
      next: (res) => { this.session.setAuth(res.token, res.customer); this.busy = false; this.step = 'location'; this.cdr.markForCheck(); },
      error: (e) => this.fail(e),
    });
  }

  // ── forgot: set a new password, then sign in ──
  submitReset(): void {
    if (!this.pwOk || !this.pwMatch) { this.tried = true; return; }
    this.busy = true; this.error = '';
    const newPw = this.pw;
    this.auth.passwordReset(this.phone, this.resetToken, newPw).subscribe({
      next: () => {
        this.auth.login(this.phone, newPw).subscribe({
          next: (res) => { this.session.setAuth(res.token, res.customer); this.busy = false; this.ui.completeLogin(); },
          error: (e) => this.fail(e),
        });
      },
      error: (e) => this.fail(e),
    });
  }

  // ── location gate: request the real device location on the user's tap ──
  // (Triggering getCurrentPosition here — inside the click handler — is the
  // reliable moment for the browser permission prompt. Doing it later, after the
  // sheet closes, is what left the popup stranded.)
  allowLocation(): void {
    if (!navigator.geolocation) { this.finish(); return; }
    this.locState = 'locating'; this.cdr.markForCheck();
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.session.setCoords(pos.coords.latitude, pos.coords.longitude);
        this.locState = 'granted'; this.cdr.markForCheck();
      },
      () => {
        // Denied / unavailable / timed out — don't trap the user; let them retry
        // or continue (delivery-zone accuracy just won't be available yet).
        this.locState = 'denied'; this.cdr.markForCheck();
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }
  finish(): void { this.ui.completeLogin(); }
}
